import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Shield, Star, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { 
  fadeInUp, 
  fadeInLeft, 
  fadeInRight, 
  scaleIn,
  staggerContainer,
  staggerItem,
  viewportSettings
} from '../hooks/useScrollAnimation';

export const Home = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      video.currentTime = 0;
      video.play();
    };

    video.addEventListener('ended', handleEnded);
    
    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Barry Howell for Marshall County Jailer"
        description="Barry Howell is running for Marshall County Jailer. With 15 years of service, he brings safety, integrity, and professional leadership to Marshall County, KY."
        keywords="Marshall County Jailer, Barry Howell, Marshall County Jail, KY, election, candidate, mcjailer, mc jail"
        canonical="https://mcjailer.com/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Barry Howell",
            "jobTitle": "Candidate for County Jailer",
            "affiliation": {
              "@type": "Organization",
              "name": "Marshall County Sheriff's Office"
            },
            "url": "https://mcjailer.com",
            "image": "https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/ya6nzdiw_DSC09954.jpg",
            "sameAs": [
              "https://www.facebook.com/people/Barry-Howell-For-Marshall-County-Jailer/61582275503939/"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Benton",
              "addressRegion": "KY",
              "addressCountry": "US"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Barry Howell for Marshall County Jailer",
            "url": "https://mcjailer.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://mcjailer.com/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section with Video */}
      <section className="relative pt-20">
        <div className="relative w-full aspect-[16/9] md:aspect-video md:max-h-[80vh] bg-[#0A0A0A] overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
            data-testid="campaign-video"
          >
            <source src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/vauoty3h_Logo_Indent2.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-0 right-0 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 px-4 sm:px-6">
            <Link to="/platform">
              <Button 
                data-testid="hero-platform-btn"
                className="bg-[#DAA520] text-black hover:bg-[#E6B422] font-bold uppercase tracking-wider px-5 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg gold-glow w-full sm:w-auto"
              >
                View Platform
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/stay-informed">
              <Button 
                data-testid="hero-support-btn"
                variant="outline"
                className="border-white/40 text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-5 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg backdrop-blur-sm bg-black/20 w-full sm:w-auto"
              >
                Support Campaign
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={staggerContainer}
          >
            <motion.div 
              variants={staggerItem}
              className="bg-[#111111] border border-[#DAA520]/20 p-6 md:p-8 hover:border-[#DAA520]/50 transition-colors platform-card" 
              data-testid="mission-safety"
            >
              <Shield className="w-10 h-10 md:w-12 md:h-12 text-[#DAA520] mb-4 md:mb-6" />
              <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide font-['Barlow_Condensed'] mb-3 md:mb-4">
                Safety First
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                Protecting the public starts inside the jail. Strong leadership and clear accountability for all.
              </p>
            </motion.div>
            
            <motion.div 
              variants={staggerItem}
              className="bg-[#111111] border border-[#DAA520]/20 p-6 md:p-8 hover:border-[#DAA520]/50 transition-colors platform-card" 
              data-testid="mission-leadership"
            >
              <Star className="w-10 h-10 md:w-12 md:h-12 text-[#DAA520] mb-4 md:mb-6" />
              <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide font-['Barlow_Condensed'] mb-3 md:mb-4">
                Professional Leadership
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                Leading by example with respect, teamwork, and professionalism at every level.
              </p>
            </motion.div>
            
            <motion.div 
              variants={staggerItem}
              className="bg-[#111111] border border-[#DAA520]/20 p-6 md:p-8 hover:border-[#DAA520]/50 transition-colors platform-card sm:col-span-2 md:col-span-1" 
              data-testid="mission-community"
            >
              <Users className="w-10 h-10 md:w-12 md:h-12 text-[#DAA520] mb-4 md:mb-6" />
              <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide font-['Barlow_Condensed'] mb-3 md:mb-4">
                Community Partnership
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                Working together with local law enforcement, courts, and community organizations.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-12 md:py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <motion.div 
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInLeft}
            >
              <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 md:mb-4">
                About Barry
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 md:mb-6">
                15 Years of Service
              </h2>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-6">
                A lifelong resident of Marshall County, Barry Howell has dedicated 15 years to serving with the Marshall County Sheriff's Office. His commitment to public safety and community service makes him the right choice for County Jailer.
              </p>
              <Link to="/about">
                <Button 
                  data-testid="about-preview-btn"
                  variant="outline"
                  className="border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-black font-bold uppercase tracking-wider"
                >
                  Learn More About Barry
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              className="flex-1 w-full max-w-sm md:max-w-none"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInRight}
            >
              <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-[#DAA520]/30 group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/ya6nzdiw_DSC09954.jpg"
                  alt="Barry Howell and his wife"
                  className="w-full h-full object-cover object-top brightness-110 contrast-105 group-hover:scale-105 transition-transform duration-500"
                  data-testid="barry-wife-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facebook Feed Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center mb-8 md:mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 md:mb-4">
              Stay Connected
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed']">
              Follow the Campaign
            </h2>
          </motion.div>
          
          <motion.div 
            className="max-w-[500px] mx-auto w-full"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={scaleIn}
            data-testid="facebook-feed"
          >
            {/* Professional Card Container */}
            <div className="bg-white rounded-xl overflow-hidden shadow-2xl border border-white/10">
              {/* Custom Header to make it look less generic */}
              <div className="bg-[#1877F2] py-4 px-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6 text-white fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.962.925-1.962 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-white font-bold text-lg tracking-wide font-['Barlow_Condensed'] uppercase">Official Campaign Page</span>
                </div>
                <a 
                  href="https://www.facebook.com/people/Barry-Howell-For-Marshall-County-Jailer/61582275503939" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/90 hover:text-white text-xs font-semibold uppercase tracking-wider transition-colors"
                >
                  View Page &rarr;
                </a>
              </div>

              {/* The Feed */}
              <div className="bg-white">
                <iframe
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2F61582275503939&tabs=timeline&width=500&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                  width="100%"
                  height="600"
                  style={{ border: 'none', overflow: 'hidden' }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Barry Howell for Marshall County Jailer Facebook Feed"
                  className="w-full"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-[#111111] to-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 md:mb-6">
              Join the Movement
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto mb-6 md:mb-8">
              Help Barry Howell bring experienced leadership to Marshall County. Your support makes a difference.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/stay-informed">
                <Button 
                  data-testid="cta-signup-btn"
                  className="bg-[#DAA520] text-black hover:bg-[#E6B422] font-bold uppercase tracking-wider px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg gold-glow w-full sm:w-auto"
                >
                  Get Involved
                </Button>
              </Link>
              <Button 
                onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=YMDPJ3HQR2LNL', '_blank')}
                data-testid="cta-donate-btn"
                variant="outline"
                className="border-white/30 text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
              >
                Donate Now
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
